import {
  Box, Card, CardContent, Typography, TextField, Button, Grid, Alert,
  CircularProgress, Switch, FormControlLabel, Divider,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../../api/axios';

export default function SaasSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/superadmin/settings')
      .then(r => setSettings(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await api.put('/superadmin/settings', settings);
      setMsg('Settings saved successfully.');
    } catch(e) { setMsg('Error: ' + (e.response?.data?.message || 'Failed')); }
    setSaving(false);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>Platform Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Global SaaS configuration</Typography>

      {msg && <Alert severity={msg.startsWith('Error') ? 'error' : 'success'} sx={{ mb: 3 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Grid container spacing={3}>
        {/* General */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>General</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Platform Name" value={settings?.platform_name || ''} onChange={e => set('platform_name', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Support Email" value={settings?.support_email || ''} onChange={e => set('support_email', e.target.value)} type="email" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Currency" value={settings?.currency || 'PKR'} onChange={e => set('currency', e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Default Trial Days" type="number" value={settings?.trial_days || 14} onChange={e => set('trial_days', e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Tax Rate (%)" type="number" value={settings?.tax_rate || 0} onChange={e => set('tax_rate', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={settings?.maintenance_mode === '1' || settings?.maintenance_mode === true} onChange={e => set('maintenance_mode', e.target.checked ? '1' : '0')} />}
                    label={<Box><Typography variant="body2">Maintenance Mode</Typography><Typography variant="caption" color="text.secondary">Disables tenant access while maintenance is ongoing</Typography></Box>}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* SMTP */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>SMTP / Email</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="SMTP Host" value={settings?.smtp_host || ''} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.gmail.com" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="SMTP Port" type="number" value={settings?.smtp_port || 587} onChange={e => set('smtp_port', e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Encryption" value={settings?.smtp_encryption || 'TLS'} onChange={e => set('smtp_encryption', e.target.value)} placeholder="TLS / SSL" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="SMTP Username" value={settings?.smtp_username || ''} onChange={e => set('smtp_username', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="SMTP Password" type="password" value={settings?.smtp_password || ''} onChange={e => set('smtp_password', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="From Name" value={settings?.smtp_from_name || ''} onChange={e => set('smtp_from_name', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="From Email" value={settings?.smtp_from_email || ''} onChange={e => set('smtp_from_email', e.target.value)} type="email" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* WhatsApp */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>WhatsApp API</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="WhatsApp API URL" value={settings?.whatsapp_api_url || ''} onChange={e => set('whatsapp_api_url', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="WhatsApp API Key" value={settings?.whatsapp_api_key || ''} onChange={e => set('whatsapp_api_key', e.target.value)} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Billing Config</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Invoice Prefix" value={settings?.invoice_prefix || 'INV'} onChange={e => set('invoice_prefix', e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Payment Due Days" type="number" value={settings?.payment_due_days || 7} onChange={e => set('payment_due_days', e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Renewal Reminder Days" type="number" value={settings?.renewal_reminder_days || 7} onChange={e => set('renewal_reminder_days', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Bank Account Name" value={settings?.bank_account_name || ''} onChange={e => set('bank_account_name', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Bank Account Number" value={settings?.bank_account_number || ''} onChange={e => set('bank_account_number', e.target.value)} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" size="large" startIcon={<Save />} onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </Box>
    </Box>
  );
}
